/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  startOfMonth,
  format,
  addMonths,
  endOfMonth,
  differenceInMonths,
} from "date-fns";
import * as htmlToImage from "html-to-image";

import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";

import CopyIcon from "@mui/icons-material/FileCopy";
import DownloadIcon from "@mui/icons-material/Download";

import { ThemeProvider } from "@mui/material";
import CloseIcon from "../../../../../components/CloseIcon";
import FormField from "../../../../../components/FormField";
import IGuidance from "../../../types/IGuidance";
import GuidanceTableTheme from "../../../styles/tableTheme";
import { useIsMobile } from "../../../../../hooks/useIsMobile";
import { defaultTheme } from "../../../../../styles/Theme";
import { useNitroStore } from "../../../../../store/Store";
import MonthFilter from "./monthFilter";
import { useIsComponentMounted } from "../../../../../hooks/useIsComponentMounted";

import "./screenshot.css";
import GuidanceService from "../../../lib/guidance.service";
import LoadingIcon from "../../../../../components/Loading";

const currentMonth = startOfMonth(new Date());

const months = [
  {
    label: "",
    value: "",
    group: "",
  },
  ...Array.from({ length: 12 }, (_, i) => {
    const dt = addMonths(currentMonth, i);
    return {
      label: format(dt, "MMMM"),
      value: format(dt, "yyyy-MM-dd"),
      group: format(dt, "yyyy"),
    };
  }),
];

const GuidanceScreenshot = ({
  location,
  product,
  close,
}: {
  location: string;
  product: string;
  close: () => void;
}) => {
  const isMobile = useIsMobile(660);

  const isComponentMounted = useIsComponentMounted();
  const isTablet = useIsMobile(1430);

  const [savedNotification, failedNotification] = useNitroStore((state) => [
    state.savedNotification,
    state.failedNotification,
  ]);

  const [start, setStart] = useState<{
    label: string;
    value: string;
    group: string;
  }>({
    label: "",
    value: "",
    group: "",
  });
  const [end, setEnd] = useState<{
    label: string;
    value: string;
    group: string;
  }>({
    label: "",
    value: "",
    group: "",
  });
  const [comment, setComment] = useState<string>("");

  const [filteredRecords, setFilteredRecords] = useState<IGuidance[] | null>(
    null
  );

  const [isLoading, setIsLoading] = useState(false);
  const loadRecords = useCallback(async () => {
    setFilteredRecords(null);
    if (!end || !end.value) return;

    setIsLoading(true);
    const startDt = new Date(`${start.value}T00:00`);
    const endDt = new Date(`${end.value}T00:00`);

    try {
      const result = await GuidanceService.getIndicationsByMonth(
        location,
        product,
        format(startDt, "yyyy-MM-dd"),
        format(endDt, "yyyy-MM-dd")
      );

      const newRecords = new Array(differenceInMonths(endDt, startDt) + 1)
        .fill(0)
        .map((_, i) => {
          const dt = format(addMonths(startDt, i), "yyyy-MM-dd");
          const record = result.find((r) => {
            return r.date === dt;
          });

          if (!record) {
            return {
              date: dt,
              fobPrice: 0,
              altPrice: 0,
            };
          }

          return record;
        });

      setFilteredRecords(newRecords);
    } catch (err) {
      console.log(err);
    }
    setIsLoading(false);
  }, [start, end, location, product]);

  useEffect(() => {
    loadRecords();
  }, [location, product, start, end, loadRecords]);

  const copyToClipboard = async () => {
    try {
      if (!filteredRecords) return;

      const element = document.getElementById("screenshotElement");
      if (!element) return;

      const blob = await htmlToImage.toBlob(element, { skipFonts: true });
      if (!blob) {
        failedNotification("Failed to create blob", "error");
        return;
      }

      const { userAgent } = navigator;
      if (/Mobi|Android/i.test(userAgent)) {
        try {
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "Guidance.png";

          document.body.appendChild(link);
          link.dispatchEvent(
            new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              view: window,
            })
          );
          document.body.removeChild(link);
        } catch (err) {
          console.error(err);
          failedNotification("Failed to download", (err as any).message);
        }
      } else {
        try {
          await navigator.clipboard.write([
            new ClipboardItem(
              {
                [blob.type]: blob,
              }
              // Object.defineProperty({}, blob?.type as any, {
              //   value: blob,
              //   enumerable: true,
              // })
            ),
          ]);
          savedNotification("Successfully copied");
          setHasCopied(true);
        } catch (err) {
          console.error(err);
          failedNotification("Failed to copy", (err as any).message);
        }
      }
    } catch (err) {
      console.error(err);
      failedNotification("Failed to generate image", (err as any).message);
    }
  };

  const toRef = useRef<any>(null);

  const [hasCopied, setHasCopied] = useState(true);
  const [showCopyWarning, setShowCopyWarning] = useState(false);
  useEffect(() => {
    if (!isComponentMounted) return;
    setHasCopied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment]);
  const attemptClose = () => {
    if (hasCopied || !start.value || !end.value) {
      close();
    } else {
      setShowCopyWarning(true);
    }
  };

  return (
    <div className="guidanceScreenshot">
      <div
        style={{
          minWidth: isMobile ? "100%" : 440,
          maxWidth: isMobile ? "100%" : 600,
        }}
      >
        <div
          style={{
            width: "100%",
            backgroundColor: "#F7F7F7",
            boxShadow: "0px 4px 4px 0px #0000004d, 0px 8px 12px 6px #00000026",
            borderRadius: isTablet ? 0 : 20,
            position: "relative",
          }}
        >
          {showCopyWarning && (
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1,
                backgroundColor: "white",
                borderRadius: 8,
                padding: 30,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 20,
              }}
            >
              <div
                style={{
                  fontSize: "1.2em",
                  fontStyle: "italic",
                  color: "#595959",
                  textAlign: "center",
                }}
              >
                Are you sure you want to close without copying?
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <Button
                  id="screenshotCancelButton"
                  size="large"
                  variant="outlined"
                  onClick={() => setShowCopyWarning(false)}
                >
                  NO
                </Button>
                <Button
                  id="screnshotCopyWarningButton"
                  size="large"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setShowCopyWarning(false);
                    close();
                  }}
                >
                  YES
                </Button>
              </div>

              <Button
                id="screenshotCopyAndCloseButton"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
                variant="contained"
                color="success"
                disabled={!filteredRecords}
                onClick={() => {
                  copyToClipboard().then(() => {
                    close();
                  });
                }}
              >
                <CopyIcon style={{ fontSize: 18 }} />
                COPY AND CLOSE
              </Button>
            </div>
          )}

          {/* 
            This div is for the screenshot. HtmlToCanvas performance was poor, this is an alternative
            This is rendered off screen
          */}
          <div
            style={{
              position: "fixed",
              left: 0,
              top: -1000,
              zIndex: 1000,
              backgroundColor: "white",
            }}
          >
            <div
              id="screenshotElement"
              style={{
                backgroundColor: "white",
                border: "1px solid #D9D9D9",
                borderRadius: 12,
                display: "flex",
                flexDirection: "column",
                padding: 14,
              }}
            >
              <div
                style={{
                  paddingBottom: 4,
                  textAlign: "center",
                  fontWeight: 600,
                  fontSize: 20,
                  color: "#383838",
                }}
              >
                {location} Guidance - {product}
              </div>

              <div
                style={{
                  paddingBottom: 10,
                  textAlign: "center",
                  fontWeight: 300,
                  fontSize: 16,
                  color: "#989898",
                }}
              >
                {start.value
                  ? format(new Date(`${start.value}T00:00`), "MMMM yyyy")
                  : ""}{" "}
                -{" "}
                {end.value
                  ? format(new Date(`${end.value}T00:00`), "MMMM yyyy")
                  : ""}
              </div>

              <table>
                <thead>
                  <tr>
                    <th></th>
                    {filteredRecords?.map((r) => (
                      <th
                        style={{
                          paddingRight: 12,
                          minWidth: 90,
                          fontWeight: 600,
                          fontSize: 18,
                          textAlign: "center",
                          color: "#005CA6",
                        }}
                      >
                        {format(new Date(`${r.date}T00:00`), "MMM")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      style={{
                        height: 24,
                        paddingRight: 12,
                        fontWeight: 600,
                        fontSizeAdjust: 14,
                        color: "#383838",
                      }}
                    >
                      FOB Posted Px
                    </td>
                    {filteredRecords?.map((r) => (
                      <td
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          textAlign: "center",
                          color: "#383838",
                        }}
                      >
                        {`$${r.fobPrice}`}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td
                      style={{
                        height: 24,
                        paddingRight: 12,
                        fontWeight: 600,
                        fontSizeAdjust: 14,
                        color: "#383838",
                      }}
                    >
                      Alternative
                    </td>
                    {filteredRecords?.map((r) => (
                      <td
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          textAlign: "center",
                          color: "#383838",
                        }}
                      >
                        {`$${r.altPrice}`}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>

              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#383838",
                  marginTop: 20,
                }}
              >
                Comments
                <br />
                <div
                  style={{ fontSize: 14, fontWeight: 300, color: "#383838" }}
                >
                  {comment}
                </div>
              </div>
            </div>
          </div>

          {/* HEADER */}
          <div className="guidanceScreenshotHeader">
            Guidance Screenshot
            <div style={{ position: "absolute", right: 8, top: 8 }}>
              <CloseIcon onClick={attemptClose} />
            </div>
          </div>
          {/* END HEADER */}

          <div className="guidanceScreenshotBody">
            <div
              id="guidanceScreenshot"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              <ThemeProvider theme={defaultTheme}>
                <FormField label="Window">
                  <div style={{ display: "flex", gap: 10 }}>
                    <MonthFilter
                      placeholder="From"
                      value={start}
                      setValue={(value) => {
                        setStart(value);
                        setEnd({ label: "", value: "", group: "" });

                        toRef.current?.focus();
                      }}
                      options={months}
                    />
                    <MonthFilter
                      inputRef={toRef}
                      placeholder="To"
                      value={end}
                      setValue={setEnd}
                      options={months.filter((r) => {
                        if (r.value === "") return true;

                        if (!start) return true;
                        return r.value >= start.value;
                      })}
                    />
                  </div>
                </FormField>
                <ThemeProvider theme={GuidanceTableTheme}>
                  <div style={{ width: "100%" }}>
                    <Table>
                      <TableHead>
                        <TableRow style={{ height: 34 }}>
                          <TableCell
                            className="reportHeader"
                            colSpan={20}
                            style={{
                              textAlign: "center",
                              fontWeight: 500,
                              color: "#383838",

                              fontSize: "1.1em",
                            }}
                          >{`${location} Guidance - ${product}`}</TableCell>
                        </TableRow>
                      </TableHead>
                    </Table>

                    <div
                      style={{
                        maxWidth: "calc(100% - 0px)",
                        overflowX: "auto",
                      }}
                    >
                      <Table>
                        <TableBody>
                          {isLoading && (
                            <TableRow style={{ height: 34 * 3 }}>
                              <TableCell className="reportRow" colSpan={20}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <LoadingIcon
                                    color="#005CA6"
                                    height={70}
                                    width={70}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          )}

                          {!isLoading &&
                            (!start.value ||
                              !end.value ||
                              filteredRecords === null) && (
                              <TableRow style={{ height: 34 * 3 }}>
                                <TableCell className="reportRow" colSpan={20}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    Select a window to view guidance
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          {filteredRecords && (
                            <>
                              {/* Header Row */}
                              <TableRow style={{ height: 34 }}>
                                <TableCell
                                  className="reportHeader"
                                  style={{
                                    position: "sticky",
                                    left: 0,
                                    width: 100,
                                    minWidth: 100,
                                    maxWidth: 100,
                                  }}
                                />
                                {filteredRecords.map((record) => (
                                  <TableCell className="reportHeader">
                                    {format(
                                      new Date(`${record.date}T00:00`),
                                      "MMM"
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                              {/* FOB Price Row */}
                              <TableRow style={{ height: 34 }}>
                                <TableCell
                                  className="reportRowHeader"
                                  style={{
                                    position: "sticky",
                                    left: 0,
                                  }}
                                >
                                  FOB Posted Px
                                </TableCell>
                                {filteredRecords.map((record) => (
                                  <TableCell
                                    className="reportRow"
                                    style={{
                                      color: "#383838",
                                      fontWeight: 500,
                                    }}
                                  >
                                    ${record.fobPrice}
                                  </TableCell>
                                ))}
                              </TableRow>

                              {/* Alternative Row */}
                              <TableRow style={{ height: 34 }}>
                                <TableCell
                                  className="reportRowHeader"
                                  style={{
                                    position: "sticky",
                                    left: 0,
                                  }}
                                >
                                  Alternative
                                </TableCell>
                                {filteredRecords.map((record) => (
                                  <TableCell
                                    className="reportRow"
                                    style={{
                                      color: "#383838",
                                      fontWeight: 500,
                                    }}
                                  >
                                    ${record.altPrice}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </ThemeProvider>

                <FormField label="Comments">
                  {/* <div style={{ minHeight: 140 }}> */}
                  <TextField
                    size="small"
                    minRows={1}
                    maxRows={4}
                    multiline
                    fullWidth
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  {/* </div> */}
                </FormField>
              </ThemeProvider>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {/* <Button
                onClick={() => {
                  navigator.permissions.query({
                    name: "",
                  });
                }}
              >
                Test
              </Button> */}
              <Button
                id="screenshotCopyToClipboardButton"
                disabled={isLoading || !start.value || !end.value}
                variant="contained"
                color="success"
                onClick={copyToClipboard}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {/Mobi|Android/i.test(navigator.userAgent) && (
                  <>
                    <DownloadIcon style={{ fontSize: 18 }} />
                    DOWNLOAD IMAGE
                  </>
                )}
                {!/Mobi|Android/i.test(navigator.userAgent) && (
                  <>
                    <CopyIcon style={{ fontSize: 18 }} />
                    COPY TO CLIPBOARD
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidanceScreenshot;
